import { useState } from "react";
import { Button, Card, InputText, Stack, Text } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { AirlinePicker } from "../AirlinePicker";
import { useCreateUserMutation } from "../../hooks/useCreateUserMutation";
import { useAirlinesQuery } from "../../hooks/useUserAirlineGroupsQuery";
import type { Airline } from "../../types";
import { isValidEmail } from "../../utils/email";
import styles from "./AddUserForm.module.css";

type AddUserFormProps = {
  onSuccess: (email: string) => void;
};

export const AddUserForm = ({ onSuccess }: AddUserFormProps) => {
  const i18n = useI18n();

  const [airline, setAirline] = useState<Airline | null>(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);

  const { data: airlines, isLoading } = useAirlinesQuery();
  const { mutate, isPending, error } = useCreateUserMutation();

  const handleSubmit = () => {
    if (!airline || !email) return;
    if (!isValidEmail(email)) {
      setEmailError(true);
      return;
    }
    setEmailError(false);
    mutate({ email, airlineCode: airline.code }, { onSuccess: () => onSuccess(email) });
  };

  return (
    <Card mixin={{ padding: 6 }}>
      <Stack gap={4} className={styles.userForm}>
        <AirlinePicker
          airlines={airlines}
          isLoading={isLoading}
          value={airline?.code}
          label={i18n.trans(t("flights_airline_portal_add_user_dropdown_label"))}
          placeholder={i18n.trans(t("flights_airline_portal_add_user_dropdown_placeholder"))}
          hideSubmitButton
          onChange={setAirline}
        />

        <Stack gap={1}>
          <Text variant="emphasized_2">{i18n.trans(t("flights_airline_portal_add_user_email_label"))}</Text>
          <InputText
            name="email"
            size="large"
            placeholder={i18n.trans(t("flights_airline_portal_add_user_email_placeholder"))}
            value={email}
            onChange={({ value }: { value: string }) => {
              setEmail(value);
              if (emailError) setEmailError(false);
            }}
            error={emailError ? i18n.trans(t("flights_airline_portal_add_user_email_error")) : undefined}
          />
        </Stack>

        {error && (
          <Text variant="body_2" color="destructive">
            {i18n.trans(t("flights_airline_portal_add_user_general_error"))}
          </Text>
        )}

        <Button size="large" onClick={handleSubmit} disabled={!airline || !email || isPending}>
          {i18n.trans(t("flights_airline_portal_add_user_button"))}
        </Button>
      </Stack>
    </Card>
  );
};
